import {
  AttachmentsScope,
  ImageDisplayStyle,
} from '../../types/model/common';
import { PdfDocumentCounts } from '../classifyDocumentsForPdfEstimate';
import { DIVIDER_HEIGHT } from './pdfLayoutConstants';
import {
  LayoutNode,
  column,
  divider,
  leaf,
  nextPage,
  singlePage,
} from './pdfLayoutEngine';

export const SECTION_HEADER_HEIGHT = 34;
const LIST_ROW_HEIGHT = 17;
const MINIATURE_ROW_HEIGHT = 210;
const FULL_IMAGE_HEIGHT = 560;

function listRowNodes(count: number): LayoutNode[] {
  const nodes: LayoutNode[] = [];
  for (let i = 0; i < count; i += 1) {
    nodes.push(leaf(LIST_ROW_HEIGHT));
  }
  return nodes;
}

function miniatureSectionNodes(
  imageCount: number,
  includeSectionHeaders: boolean
): LayoutNode[] {
  if (imageCount <= 0) {
    return [];
  }
  const rowCount = Math.ceil(imageCount / 3);
  const rows: LayoutNode[] = [];
  for (let i = 0; i < rowCount; i += 1) {
    rows.push(leaf(MINIATURE_ROW_HEIGHT));
  }
  const headerNodes = includeSectionHeaders
    ? [leaf(SECTION_HEADER_HEIGHT), divider(DIVIDER_HEIGHT)]
    : [];
  // Header + divider + first row are kept together; remaining rows flow and break.
  return [
    column([
      singlePage([...headerNodes, rows[0]]),
      ...rows.slice(1),
    ]),
  ];
}

function fullPageSectionNodes(
  imageCount: number,
  includeSectionHeaders: boolean,
  flowContentAcrossPages: boolean
): LayoutNode[] {
  if (imageCount <= 0) {
    return [];
  }
  // The whole image block starts on a fresh page and the next section starts fresh too,
  // matching the per-picture page breaks emitted by the backend in full-A4 mode.
  // In the communication section content flows instead: no leading/trailing break, only
  // breaks between consecutive images, so the following content fills the remaining space.
  const nodes: LayoutNode[] = [];
  if (!flowContentAcrossPages) {
    nodes.push(nextPage());
  }
  if (includeSectionHeaders) {
    nodes.push(leaf(SECTION_HEADER_HEIGHT));
  }
  for (let i = 0; i < imageCount; i += 1) {
    if (i > 0) {
      nodes.push(nextPage());
    }
    nodes.push(leaf(FULL_IMAGE_HEIGHT));
  }
  if (!flowContentAcrossPages) {
    nodes.push(nextPage());
  }
  return nodes;
}

export interface DocumentSectionNodesOptions {
  counts: PdfDocumentCounts;
  displayStyle: ImageDisplayStyle;
  scope: AttachmentsScope;
  hasMainImage: boolean;
  /**
   * False for the communication section, where attachments render directly under the entry
   * without the "pictures" / "other attachments" subsection headers. Defaults to true.
   */
  includeSectionHeaders?: boolean;
  /**
   * True for the communication section, where full-page images flow into the surrounding
   * content (no leading/trailing page break, only breaks between images). Defaults to false.
   */
  flowContentAcrossPages?: boolean;
}

/**
 * Nodes for a documents section (issue attachments, linked documentation or message
 * attachments). Images render per `displayStyle`; non-images always render as a list.
 */
export function documentSectionNodes({
  counts,
  displayStyle,
  scope,
  hasMainImage,
  includeSectionHeaders = true,
  flowContentAcrossPages = false,
}: DocumentSectionNodesOptions): LayoutNode[] {
  let imageCount = counts.images;
  if (scope === AttachmentsScope.main) {
    imageCount = Math.min(
      1,
      counts.images > 0 ? counts.images : hasMainImage ? 1 : 0
    );
  }
  const nonImageCount =
    scope === AttachmentsScope.main ? 0 : counts.nonImages;

  if (imageCount === 0 && nonImageCount === 0) {
    return [];
  }

  if (displayStyle === ImageDisplayStyle.list) {
    return [
      ...(includeSectionHeaders ? [leaf(SECTION_HEADER_HEIGHT)] : []),
      ...listRowNodes(imageCount + nonImageCount),
    ];
  }

  const nodes: LayoutNode[] = [];
  if (imageCount > 0) {
    if (displayStyle === ImageDisplayStyle.fullPage) {
      nodes.push(
        ...fullPageSectionNodes(
          imageCount,
          includeSectionHeaders,
          flowContentAcrossPages
        )
      );
    } else {
      nodes.push(...miniatureSectionNodes(imageCount, includeSectionHeaders));
    }
  }
  if (nonImageCount > 0) {
    nodes.push(
      ...(includeSectionHeaders ? [leaf(SECTION_HEADER_HEIGHT)] : []),
      ...listRowNodes(nonImageCount)
    );
  }
  return nodes;
}
